import { PathElement } from './path-element.ts';
import { MatchingContext } from '../path-pattern.ts';
import { isPathSegment } from '../../../../http/server/path.ts';
import type { PathContainerElement } from '../../../../../types/path';

/*internal*/ export class CaptureSegmentsPathElement extends PathElement {
    readonly #variableName: string;

    constructor(pos: number, captureDescriptor: Uint16Array, separator: number) {
        super(pos, separator);
        this.#variableName = String.fromCharCode(...captureDescriptor.slice(2, captureDescriptor.length - 1));
    }

    matches(pathIndex: number, matchingContext: MatchingContext): boolean {
        if (pathIndex === 0 && this.next !== undefined) {
            let endPathIndex = pathIndex;
            while (endPathIndex < matchingContext.pathLength) {
                if (this.next.matches(endPathIndex, matchingContext)) {
                    this.#collectParameters(matchingContext, pathIndex, endPathIndex);
                    return true;
                }
                endPathIndex++;
            }
            return false;
        }
        else if (pathIndex < matchingContext.pathLength && !matchingContext.isSeparator(pathIndex)) {
            return false;
        }
        if (matchingContext.determinedRemainingPath) {
            matchingContext.remainingPathIndex = matchingContext.pathLength;
        }
        this.#collectParameters(matchingContext, pathIndex, matchingContext.pathLength);
        return true;
    }

    #collectParameters(matchingContext: MatchingContext, startPathIndex: number, endPathIndex: number) {
        if (matchingContext.extractingVariables) {
            // collect the parameters from all the remaining path segments
            let parametersCollector = PathElement.NO_PARAMETERS;
            for (let i = startPathIndex; i < endPathIndex; i++) {
                const element = matchingContext.pathElements[i];
                if (isPathSegment(element)) {
                    const { parameters } = element;
                    if (parameters.size > 0) {
                        if (parametersCollector === PathElement.NO_PARAMETERS) {
                            parametersCollector = new Map<string, string[]>();
                        }
                        for (const [key, values] of parameters) {
                            if (parametersCollector.has(key)) {
                                const existingValues = parametersCollector.get(key)!;
                                parametersCollector.set(key, existingValues.concat(values));
                            }
                            else {
                                parametersCollector.set(key, values);
                            }
                        }
                    }
                }
            }
            matchingContext.set(
                this.#variableName,
                this.#pathToString(startPathIndex, endPathIndex, matchingContext.pathElements), parametersCollector);
        }
    }

    #pathToString(fromSegment: number, toSegment: number, pathElements: readonly PathContainerElement[]): string {
        const sb: string[] = [];
        for (let i = fromSegment; i <  toSegment; i++) {
            const element = pathElements[i];
            if (isPathSegment(element)) {
                sb.push(element.valueToMatch);
            }
            else {
                sb.push(element.value);
            }
        }
        return sb.join('');
    }

    get normalizedLength() {
        return 1;
    }

    get chars(): Uint16Array {
        const s = `${String.fromCharCode(this.separator)}{*${this.#variableName}}`;
        const result = new Uint16Array(s.length);
        for (let i = 0; i < s.length; i++) {
            result[i] = s.charCodeAt(i);
        }
        return result;
    }

    get wildcardCount(): number {
        return 0;
    }

    get captureCount(): number {
        return 1;
    }

    toString() {
        return `CaptureSegments(${String.fromCharCode(this.separator)}{*${this.#variableName}})`;
    }
}
